// frontend/src/components/admin/AdminPortal.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as api from '../../api';
import SubscriptionBillingTab from './SubscriptionBillingTab';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, ComposedChart
} from 'recharts';
import { fmt } from '../common/CommonComponents';

const COLORS = ['#e94560', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4', '#F97316', '#EC4899'];

// ── CSV export helper ────────────────────────────────────────────
// Plain-CSV (no library needed) — escapes quotes/commas/newlines per RFC 4180.
function exportToCSV(rows, filename) {
  if (!rows || !rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (val) => {
    const s = val === null || val === undefined ? '' : String(val);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [
    headers.join(','),
    ...rows.map(row => headers.map(h => escape(row[h])).join(','))
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function ExportButton({ rows, filename, label = 'Export CSV' }) {
  return (
    <button
      className="ap-export-btn"
      onClick={() => exportToCSV(rows, filename)}
      disabled={!rows || !rows.length}
      title={!rows || !rows.length ? 'Nothing to export yet' : `Download ${filename}.csv`}
    >
      <span className="ap-export-icon">⇩</span> {label}
    </button>
  );
}

// ── Scoped admin theme (injected once) ───────────────────────────
const ADMIN_CSS = `
.ap-root {
  --ap-bg: #0a0d14;
  --ap-surface: #11151f;
  --ap-surface-2: #161b27;
  --ap-border: #232938;
  --ap-text: #e8eaf0;
  --ap-text-dim: #8b92a8;
  --ap-text-faint: #565d72;
  --ap-accent: #e94560;
  --ap-accent-glow: rgba(233,69,96,0.25);
  --ap-success: #10b981;
  --ap-warn: #f59e0b;
  --ap-danger: #ef4444;
  --ap-info: #3b82f6;
  background: var(--ap-bg);
  color: var(--ap-text);
  min-height: 100vh;
  font-family: inherit;
  display: flex;
  align-items: stretch;
}
[data-theme="light"] .ap-root {
  --ap-bg: #f4f5f8;
  --ap-surface: #ffffff;
  --ap-surface-2: #f0f1f5;
  --ap-border: #e2e4ea;
  --ap-text: #14161f;
  --ap-text-dim: #5a5f72;
  --ap-text-faint: #9aa0b4;
  --ap-accent-glow: rgba(233,69,96,0.12);
}

/* ── Sidebar ── */
.ap-sidebar {
  width: 232px;
  flex-shrink: 0;
  background: var(--ap-surface);
  border-right: 1px solid var(--ap-border);
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  z-index: 20;
  transition: transform .2s ease;
}
.ap-sidebar-head {
  padding: 22px 20px 18px;
  border-bottom: 1px solid var(--ap-border);
}
.ap-sidebar-head .ap-eyebrow {
  font-size: 10px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--ap-accent);
  font-weight: 700;
  margin-bottom: 4px;
}
.ap-sidebar-head h1 {
  font-size: 17px;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.2px;
}
.ap-sidebar-head p {
  font-size: 11px;
  color: var(--ap-text-faint);
  margin: 4px 0 0;
  line-height: 1.4;
}
.ap-nav {
  flex: 1;
  padding: 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
}
.ap-nav-item {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 12px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--ap-text-dim);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  position: relative;
  transition: background .12s ease, color .12s ease;
}
.ap-nav-item:hover { background: var(--ap-surface-2); color: var(--ap-text); }
.ap-nav-item.active {
  background: var(--ap-accent-glow);
  color: var(--ap-text);
}
.ap-nav-item.active::before {
  content: '';
  position: absolute;
  left: -10px;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--ap-accent);
  box-shadow: 0 0 10px var(--ap-accent-glow);
}
.ap-nav-icon { font-size: 15px; width: 18px; text-align: center; flex-shrink: 0; }
.ap-nav-badge {
  margin-left: auto;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 20px;
  background: var(--ap-surface-2);
  color: var(--ap-text-dim);
}
.ap-nav-item.active .ap-nav-badge { background: var(--ap-accent); color: #fff; }

.ap-sidebar-foot {
  padding: 14px 16px;
  border-top: 1px solid var(--ap-border);
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ap-theme-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  color: var(--ap-text-dim);
}
.ap-theme-toggle button {
  border: 1px solid var(--ap-border);
  background: var(--ap-surface-2);
  color: var(--ap-text);
  font-size: 13px;
  width: 30px;
  height: 26px;
  border-radius: 7px;
  cursor: pointer;
}
.ap-exit-link {
  font-size: 11px;
  color: var(--ap-text-faint);
  text-decoration: none;
  cursor: pointer;
  background: none;
  border: none;
  text-align: left;
  padding: 0;
}
.ap-exit-link:hover { color: var(--ap-text-dim); }

/* ── Mobile sidebar toggle ── */
.ap-mobile-bar { display: none; }
@media (max-width: 880px) {
  .ap-sidebar { position: fixed; transform: translateX(-100%); }
  .ap-sidebar.open { transform: translateX(0); box-shadow: 0 0 40px rgba(0,0,0,.5); }
  .ap-mobile-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: var(--ap-surface);
    border-bottom: 1px solid var(--ap-border);
    position: sticky;
    top: 0;
    z-index: 15;
  }
  .ap-mobile-bar button {
    background: var(--ap-surface-2);
    border: 1px solid var(--ap-border);
    color: var(--ap-text);
    width: 34px; height: 34px;
    border-radius: 8px;
    font-size: 16px;
    cursor: pointer;
  }
  .ap-mobile-bar strong { font-size: 14px; }
  .ap-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,.5); z-index: 19;
  }
}

/* ── Main ── */
.ap-main { flex: 1; min-width: 0; }
.ap-content { max-width: 1280px; margin: 0 auto; padding: 28px 32px 60px; }
@media (max-width: 600px) { .ap-content { padding: 18px 16px 40px; } }

.ap-page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 24px;
}
.ap-page-head h2 {
  font-size: 22px;
  font-weight: 800;
  margin: 0 0 4px;
  letter-spacing: -0.3px;
}
.ap-page-head .ap-sub { font-size: 12.5px; color: var(--ap-text-dim); }
.ap-controls { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }

.ap-select, .ap-search {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--ap-border);
  background: var(--ap-surface-2);
  color: var(--ap-text);
  font-size: 12.5px;
  outline: none;
}
.ap-search { min-width: 220px; }
.ap-refresh-btn {
  padding: 8px 16px;
  border-radius: 8px;
  background: var(--ap-accent);
  color: #fff;
  border: none;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}
.ap-refresh-btn:hover { filter: brightness(1.08); }
.ap-export-btn {
  padding: 8px 14px;
  border-radius: 8px;
  background: var(--ap-surface-2);
  color: var(--ap-text);
  border: 1px solid var(--ap-border);
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}
.ap-export-btn:hover:not(:disabled) { border-color: var(--ap-accent); color: var(--ap-accent); }
.ap-export-btn:disabled { opacity: .45; cursor: not-allowed; }
.ap-export-icon { font-size: 12px; }

/* ── Stat cards ── */
.ap-stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 14px;
  margin-bottom: 28px;
}
.ap-stat {
  background: var(--ap-surface);
  border: 1px solid var(--ap-border);
  border-radius: 14px;
  padding: 16px 18px;
  position: relative;
  overflow: hidden;
}
.ap-stat::after {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; height: 2px;
  background: var(--stat-color, var(--ap-accent));
  opacity: .8;
}
.ap-stat .ap-stat-label {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: .6px;
  color: var(--ap-text-faint);
  font-weight: 700;
  margin-bottom: 8px;
}
.ap-stat .ap-stat-value {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.4px;
  color: var(--ap-text);
}
.ap-stat .ap-stat-delta {
  font-size: 11px;
  font-weight: 700;
  margin-top: 6px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.ap-stat .ap-stat-delta.up { color: var(--ap-success); }
.ap-stat .ap-stat-delta.down { color: var(--ap-danger); }
.ap-stat .ap-stat-delta.flat { color: var(--ap-text-faint); }
.ap-stat-hero {
  grid-column: span 2;
  background: linear-gradient(135deg, var(--ap-surface) 0%, var(--ap-surface-2) 100%);
}
.ap-stat-hero .ap-stat-value { font-size: 32px; color: var(--ap-accent); }
@media (max-width: 600px) { .ap-stat-hero { grid-column: span 1; } }

/* ── Panels ── */
.ap-panel {
  background: var(--ap-surface);
  border: 1px solid var(--ap-border);
  border-radius: 14px;
  padding: 20px 22px;
  margin-bottom: 22px;
}
.ap-panel-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}
.ap-panel-head h3 {
  font-size: 13.5px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ap-panel-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
@media (max-width: 880px) { .ap-panel-grid-2 { grid-template-columns: 1fr; } }

.ap-empty {
  text-align: center;
  padding: 44px 20px;
  color: var(--ap-text-faint);
  font-size: 12.5px;
}
.ap-empty .ap-empty-icon { font-size: 32px; margin-bottom: 10px; opacity: .6; }

/* ── Rank list (top businesses) ── */
.ap-rank-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 11px 0;
  border-bottom: 1px solid var(--ap-border);
  cursor: pointer;
  transition: background .1s ease;
  border-radius: 8px;
}
.ap-rank-row:hover { background: var(--ap-surface-2); }
.ap-rank-row:last-child { border-bottom: none; }
.ap-rank-num {
  width: 26px; height: 26px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 11px; flex-shrink: 0;
  background: var(--ap-surface-2); color: var(--ap-text-dim);
}
.ap-rank-num.gold { background: #F59E0B; color: #fff; }
.ap-rank-num.silver { background: #9CA3AF; color: #fff; }
.ap-rank-num.bronze { background: #B45309; color: #fff; }
.ap-rank-name { font-weight: 600; font-size: 13px; }
.ap-rank-meta { font-size: 10.5px; color: var(--ap-text-faint); margin-top: 1px; }
.ap-rank-value { font-weight: 700; color: var(--ap-success); font-size: 13px; text-align: right; }
.ap-rank-sub { font-size: 10px; color: var(--ap-text-faint); text-align: right; margin-top: 1px; }

/* ── Table ── */
.ap-table-wrap { overflow-x: auto; }
.ap-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
.ap-table th {
  text-align: left;
  padding: 10px 12px;
  color: var(--ap-text-faint);
  font-weight: 700;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: .4px;
  border-bottom: 1px solid var(--ap-border);
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
}
.ap-table th:hover { color: var(--ap-text-dim); }
.ap-table td {
  padding: 12px;
  border-bottom: 1px solid var(--ap-border);
  vertical-align: middle;
}
.ap-table tr:last-child td { border-bottom: none; }
.ap-table tr:hover td { background: var(--ap-surface-2); }
.ap-biz-name { font-weight: 600; }
.ap-biz-email { font-size: 10.5px; color: var(--ap-text-faint); margin-top: 1px; }

.ap-badge {
  font-size: 10.5px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  display: inline-block;
}
.ap-badge.active { background: rgba(16,185,129,.15); color: var(--ap-success); }
.ap-badge.suspended { background: rgba(239,68,68,.15); color: var(--ap-danger); }
.ap-badge.trial { background: rgba(245,158,11,.15); color: var(--ap-warn); }

/* ── Modal ── */
.ap-modal-backdrop {
  position: fixed; inset: 0; background: rgba(5,7,12,.7);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 20px; backdrop-filter: blur(2px);
}
.ap-modal {
  background: var(--ap-surface); border: 1px solid var(--ap-border);
  border-radius: 16px; max-width: 760px; width: 100%; max-height: 88vh;
  overflow-y: auto; padding: 26px;
}
.ap-modal-head {
  display: flex; justify-content: space-between; align-items: flex-start;
  margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid var(--ap-border);
}
.ap-modal-head h2 { font-size: 18px; margin: 0 0 4px; }
.ap-modal-close {
  background: var(--ap-surface-2); border: 1px solid var(--ap-border);
  width: 30px; height: 30px; border-radius: 8px; font-size: 15px;
  color: var(--ap-text); cursor: pointer;
}
.ap-detail-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
  font-size: 12px; color: var(--ap-text-dim);
}
.ap-detail-grid > div { display: flex; gap: 6px; }
.ap-detail-grid b { color: var(--ap-text); font-weight: 600; }

/* ── Buttons ── */
.ap-btn-row { display: flex; gap: 8px; }
.ap-btn {
  padding: 9px 16px; border-radius: 8px; font-size: 12px; font-weight: 700;
  border: none; cursor: pointer;
}
.ap-btn:disabled { opacity: .45; cursor: not-allowed; }
.ap-btn-ghost { background: var(--ap-surface-2); color: var(--ap-text); border: 1px solid var(--ap-border); }
.ap-btn-approve { background: rgba(16,185,129,.15); color: var(--ap-success); }
.ap-btn-reject { background: rgba(239,68,68,.15); color: var(--ap-danger); }
.ap-btn-view { background: var(--ap-accent-glow); color: var(--ap-accent); padding: 6px 12px; font-size: 11px; }
.ap-btn-suspend { background: rgba(245,158,11,.15); color: var(--ap-warn); }
.ap-btn-activate { background: rgba(16,185,129,.15); color: var(--ap-success); }
.ap-btn-delete { background: rgba(239,68,68,.15); color: var(--ap-danger); }
.ap-btn-sm { padding: 6px 12px; font-size: 11px; }

/* ── Row actions (table + modal) ── */
.ap-row-actions { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
.ap-icon-btn {
  width: 28px; height: 28px; border-radius: 7px; border: 1px solid var(--ap-border);
  background: var(--ap-surface-2); color: var(--ap-text-dim); font-size: 12px;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: all .12s ease;
}
.ap-icon-btn:hover { color: var(--ap-text); border-color: var(--ap-text-dim); }
.ap-icon-btn.warn:hover { color: var(--ap-warn); border-color: var(--ap-warn); }
.ap-icon-btn.success:hover { color: var(--ap-success); border-color: var(--ap-success); }
.ap-icon-btn.danger:hover { color: var(--ap-danger); border-color: var(--ap-danger); }

/* ── Danger zone (delete confirmation) ── */
.ap-danger-zone {
  border: 1px solid rgba(239,68,68,.35); background: rgba(239,68,68,.06);
  border-radius: 12px; padding: 16px 18px; margin-top: 18px;
}
.ap-danger-zone h4 { color: var(--ap-danger); font-size: 12.5px; margin: 0 0 6px; }
.ap-danger-zone p { font-size: 11.5px; color: var(--ap-text-dim); margin: 0 0 12px; line-height: 1.5; }
.ap-confirm-input {
  width: 100%; padding: 9px 12px; border-radius: 8px;
  border: 1px solid var(--ap-border); background: var(--ap-surface-2);
  color: var(--ap-text); font-size: 13px; outline: none; margin-bottom: 12px;
}
.ap-confirm-input:focus { border-color: var(--ap-danger); }

.ap-profit { font-weight: 700; }
.ap-profit.positive { color: var(--ap-success); }
.ap-profit.negative { color: var(--ap-danger); }

.ap-mini-badge {
  font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 12px;
  background: var(--ap-surface-2); color: var(--ap-text-faint);
}

.ap-request-card {
  background: var(--ap-surface); border: 1px solid var(--ap-border); border-radius: 12px;
  padding: 16px 18px; margin-bottom: 10px;
}
.ap-request-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
.ap-request-name { font-weight: 700; font-size: 13.5px; }
.ap-request-meta { font-size: 11.5px; color: var(--ap-text-dim); margin-top: 3px; }
.ap-request-date { font-size: 10.5px; color: var(--ap-text-faint); margin-top: 2px; }
.ap-request-desc {
  font-size: 11.5px; color: var(--ap-text-dim); margin-top: 8px;
  padding: 9px 11px; background: var(--ap-surface-2); border-radius: 8px;
}

.ap-loading, .ap-error {
  min-height: 100vh; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 12px;
  background: var(--ap-bg); color: var(--ap-text-dim); font-size: 13px;
}
.ap-spin {
  width: 32px; height: 32px; border-radius: 50%;
  border: 3px solid var(--ap-border); border-top-color: var(--ap-accent);
  animation: ap-spin 0.8s linear infinite;
}
@keyframes ap-spin { to { transform: rotate(360deg); } }

/* ── Federation partners ── */
.ap-partner-card {
  background: var(--ap-surface); border: 1px solid var(--ap-border);
  border-radius: 12px; padding: 16px 18px; margin-bottom: 10px;
}
.ap-partner-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
.ap-partner-name { font-weight: 700; font-size: 14px; display: flex; align-items: center; gap: 8px; }
.ap-partner-source {
  font-family: 'SF Mono', Consolas, monospace; font-size: 10.5px;
  color: var(--ap-text-faint); background: var(--ap-surface-2);
  padding: 2px 7px; border-radius: 5px;
}
.ap-partner-meta { font-size: 11.5px; color: var(--ap-text-dim); margin-top: 4px; }
.ap-partner-stats {
  display: flex; gap: 18px; margin-top: 10px; padding-top: 10px;
  border-top: 1px solid var(--ap-border); flex-wrap: wrap;
}
.ap-partner-stat { font-size: 11px; color: var(--ap-text-dim); }
.ap-partner-stat b { color: var(--ap-text); font-size: 13px; display: block; }

/* Key-reveal banner — shown exactly once right after issuing/resetting a key */
.ap-key-reveal {
  background: rgba(16,185,129,.08); border: 1px solid rgba(16,185,129,.3);
  border-radius: 10px; padding: 14px 16px; margin-bottom: 16px;
}
.ap-key-reveal-warn { font-size: 11.5px; color: var(--ap-success); font-weight: 600; margin-bottom: 8px; }
.ap-key-row { display: flex; gap: 8px; align-items: center; }
.ap-key-value {
  flex: 1; font-family: 'SF Mono', Consolas, monospace; font-size: 12px;
  background: var(--ap-surface-2); border: 1px solid var(--ap-border);
  border-radius: 7px; padding: 8px 10px; color: var(--ap-text);
  overflow-x: auto; white-space: nowrap;
}
.ap-copy-btn {
  flex-shrink: 0; padding: 8px 12px; border-radius: 7px; border: 1px solid var(--ap-border);
  background: var(--ap-surface-2); color: var(--ap-text); font-size: 11.5px; font-weight: 600;
  cursor: pointer; transition: background .12s;
}
.ap-copy-btn:hover { background: var(--ap-border); }
.ap-copy-btn.copied { background: var(--ap-success); color: #fff; border-color: var(--ap-success); }
`;

export function useInjectAdminCSS() {
  useEffect(() => {
    if (document.getElementById('ap-styles')) return;
    const style = document.createElement('style');
    style.id = 'ap-styles';
    style.textContent = ADMIN_CSS;
    document.head.appendChild(style);
  }, []);
}

// ── Stat card ─────────────────────────────────────────────────
function Stat({ label, value, color, delta, hero }) {
  return (
    <div className={`ap-stat ${hero ? 'ap-stat-hero' : ''}`} style={{ '--stat-color': color }}>
      <div className="ap-stat-label">{label}</div>
      <div className="ap-stat-value">{value}</div>
      {delta !== undefined && delta !== null && (
        <div className={`ap-stat-delta ${delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'}`}>
          {delta > 0 ? '↑' : delta < 0 ? '↓' : '–'} {Math.abs(delta)}%
        </div>
      )}
    </div>
  );
}

const NAV_ITEMS = [
  { key: 'overview', icon: '◆', label: 'Overview' },
  { key: 'businesses', icon: '▦', label: 'Businesses' },
  { key: 'analytics', icon: '◢', label: 'Analytics' },
  { key: 'upgrades', icon: '◫', label: 'Subscriptions & Billing' },
  { key: 'datadeletion', icon: '🗑️', label: 'Data Deletion' },
  { key: 'outbox', icon: '✎', label: 'Email Outbox' },
  { key: 'admins', icon: '☺', label: 'Admins' },
  { key: 'health', icon: '♥', label: 'System Health' },
  { key: 'auditlog', icon: '📜', label: 'Audit Log' },
];

const adminTabFromHash = (navItems = NAV_ITEMS) => {
  const key = window.location.hash.slice(1);
  return navItems.some(item => item.key === key) ? key : 'overview';
};

function AdminPortal({ showAlert, hideAdminManagement = false, onExit = null }) {
  useInjectAdminCSS();
  const navItems = hideAdminManagement ? NAV_ITEMS.filter(item => item.key !== 'admins') : NAV_ITEMS;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businessDetail, setBusinessDetail] = useState(null);
  // Keep the admin page in the URL. A browser refresh then returns to the
  // exact admin workspace the person was using, not always Overview.
  const [activeTab, setActiveTab] = useState(() => adminTabFromHash(navItems));
  const [timeRange, setTimeRange] = useState('30d');
  const [searchTerm, setSearchTerm] = useState('');
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [sortField, setSortField] = useState('totalRevenue');
  const [sortDirection, setSortDirection] = useState('desc');
  const [chartData, setChartData] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [federationRetryId, setFederationRetryId] = useState(null);
  const [retryingFederation, setRetryingFederation] = useState(false);
  const [planUpdating, setPlanUpdating] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('erp_theme') === 'dark'; } catch (e) { return false; }
  });
  const [dataDeletionPendingCount, setDataDeletionPendingCount] = useState(0);
  // Pending ERP business subscription upgrade requests.
  const [upgradesPendingCount, setUpgradesPendingCount] = useState(0);


  const navigateToTab = useCallback((key) => {
    if (!navItems.some(item => item.key === key)) return;
    setActiveTab(key);
    if (window.location.hash !== `#${key}`) window.location.hash = key;
    setSidebarOpen(false);
  }, [navItems]);

  useEffect(() => {
    const syncTabFromUrl = () => setActiveTab(adminTabFromHash(navItems));
    window.addEventListener('hashchange', syncTabFromUrl);
    return () => window.removeEventListener('hashchange', syncTabFromUrl);
  }, [navItems]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    try { localStorage.setItem('erp_theme', darkMode ? 'dark' : 'light'); } catch (e) {}
  }, [darkMode]);

  const loadAdminData = useCallback(async () => {
    setLoading(true);
    try {
      // The businesses dataset is the core Admin Portal dependency.
      // Revenue/growth charts are supplemental and must never lock the
      // owner out of the whole portal if one analytics query has a problem.
      const allBusinesses = await api.adminGetAllBusinesses();
      setBusinesses(allBusinesses || []);

      const [overviewResult, revenueResult, growthResult] = await Promise.allSettled([
        api.adminOverview(),
        api.adminGetRevenueTrends({ range: timeRange }),
        api.adminGetGrowthMetrics()
      ]);

      if (overviewResult.status === 'fulfilled') setStats(overviewResult.value);
      const revenueData = revenueResult.status === 'fulfilled' ? revenueResult.value : null;
      const growthData = growthResult.status === 'fulfilled' ? growthResult.value : null;
      setChartData({ revenueData: revenueData?.data || [], growthData });

      const optionalFailures = [
        overviewResult.status === 'rejected' ? 'overview' : null,
        revenueResult.status === 'rejected' ? 'revenue trends' : null,
        growthResult.status === 'rejected' ? 'growth metrics' : null,
      ].filter(Boolean);
      if (optionalFailures.length) {
        console.warn('Optional admin analytics unavailable:', optionalFailures.join(', '));
      }
      setError(null);
    } catch (e) {
      console.error('Admin businesses load error:', e);
      setError(e.message);
      showAlert(e.message || 'Failed to load admin businesses', 'error');
    }
    setLoading(false);
  }, [timeRange, showAlert]);

  useEffect(() => { loadAdminData(); }, [loadAdminData]);

  // These are sidebar-wide alerts, so load them when the portal opens
  // instead of waiting for each destination page to mount first.
  const loadSidebarBadges = useCallback(async () => {
    const [upgrades, deletions] = await Promise.allSettled([
      api.adminGetUpgradeRequests('pending'),
      api.adminGetDataDeletionRequests('pending'),
    ]);
    if (upgrades.status === 'fulfilled') setUpgradesPendingCount(upgrades.value?.pendingCount || 0);
    if (deletions.status === 'fulfilled') setDataDeletionPendingCount(deletions.value?.pendingCount || 0);
  }, []);

  useEffect(() => {
    loadSidebarBadges();
    const refreshId = window.setInterval(loadSidebarBadges, 30000);
    return () => window.clearInterval(refreshId);
  }, [loadSidebarBadges]);

  const loadBusinessDetail = async (businessId) => {
    try {
      const detail = await api.adminGetBusinessDetail(businessId);
      setBusinessDetail(detail);
    } catch (e) {
      showAlert(e.message, 'error');
    }
  };

  const handleBusinessSelect = (business) => {
    setSelectedBusiness(business);
    loadBusinessDetail(business._id);
  };

  const handleCloseBusinessDetail = () => {
    setSelectedBusiness(null);
    setBusinessDetail(null);
  };

  // Refreshes the businesses list in place (and the modal's businessDetail summary)
  // without a full reload, so the table/modal reflect the new plan instantly.
  const refreshBusinessesOnly = async () => {
    try {
      const allBusinesses = await api.adminGetAllBusinesses();
      setBusinesses(allBusinesses || []);
      if (selectedBusiness) {
        const updated = (allBusinesses || []).find(b => b._id === selectedBusiness._id);
        if (updated) setSelectedBusiness(updated);
      }
    } catch (e) {
      showAlert(e.message, 'error');
    }
  };

  const handleSetPlan = async (business, plan) => {
    setPlanUpdating(business._id);
    try {
      await api.adminSetPlan(business._id, plan);
      const label = plan === 'suspended' ? 'suspended' : plan === 'active' ? 'reactivated' : 'set to trial';
      showAlert(`"${business.name}" ${label}.`, plan === 'suspended' ? 'warning' : 'success');
      await refreshBusinessesOnly();
    } catch (e) {
      showAlert(e.message, 'error');
    }
    setPlanUpdating(null);
  };

  // Clears a business's non-blocking signup risk flag (see server.js
  // POST /auth/register's flagReasons) once an admin has actually looked
  // at it — doesn't change the business's access in any way, just marks
  // the review as done so it drops off this queue.
  const handleClearFlag = async (business) => {
    setPlanUpdating(business._id);
    try {
      await api.adminClearBusinessFlag(business._id);
      showAlert(`Cleared the review flag on "${business.name}".`, 'success');
      await refreshBusinessesOnly();
    } catch (e) {
      showAlert(e.message, 'error');
    }
    setPlanUpdating(null);
  };

  // Tier 10 — subscription TIER (trial/starter/growth/enterprise), a
  // separate axis from the account-status "plan" above (active/
  // suspended/trial). No self-serve payment exists yet, so this is how
  // an admin manually applies a tier after a business pays/upgrades
  // outside the app.
  const handleSetSubscription = async (business, subscriptionTier, billingCycle) => {
    setPlanUpdating(business._id);
    try {
      await api.adminUpdateBusinessSubscription(business._id, { subscriptionTier, billingCycle });
      showAlert(`"${business.name}" moved to the ${subscriptionTier} plan (${billingCycle}).`, 'success');
      await refreshBusinessesOnly();
      if (selectedBusiness?._id === business._id) await loadBusinessDetail(business._id);
    } catch (e) {
      showAlert(e.message, 'error');
    }
    setPlanUpdating(null);
  };

  const openDeleteModal = (business) => {
    setDeleteTarget(business);
    setDeleteInput('');
  };

  const confirmDeleteBusiness = async () => {
    if (!deleteTarget || deleteInput !== deleteTarget.name) return;
    if (deleting) return;
    setDeleting(true);
    try {
      const result = await api.adminDeleteBusiness(deleteTarget._id, deleteTarget.name);
      if (result.federation?.cleaned === false) setFederationRetryId(deleteTarget._id);
      showAlert(result.message || `"${deleteTarget.name}" deleted.`, result.federation?.cleaned === false ? 'error' : 'success');
      setDeleteTarget(null);
      setDeleteInput('');
      if (selectedBusiness?._id === deleteTarget._id) handleCloseBusinessDetail();
      await refreshBusinessesOnly();
    } catch (e) {
      showAlert(`Business deletion failed: ${e.message}. The business has not been removed from this view.`, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const filteredBusinesses = useMemo(() => businesses
    .filter(b =>
      b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.ownerEmail.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(b => !onlyFlagged || b.flaggedForReview)
    .sort((a, b) => {
      const valA = a[sortField] || 0;
      const valB = b[sortField] || 0;
      if (typeof valA === 'string') return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      return sortDirection === 'asc' ? valA - valB : valB - valA;
    }), [businesses, searchTerm, onlyFlagged, sortField, sortDirection]);

  if (loading) {
    return (
      <div className="ap-loading">
        <div className="ap-spin" />
        <div>Loading admin dashboard…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="ap-error">
        <div style={{ fontSize: 32 }}>⚠</div>
        <div>{error}</div>
        <button className="ap-refresh-btn" onClick={loadAdminData}>Retry</button>
      </div>
    );
  }

  const totalBusinesses = businesses.length;
  const activeBusinesses = businesses.filter(b => b.plan === 'active').length;
  const suspendedBusinesses = businesses.filter(b => b.plan === 'suspended').length;
  const flaggedBusinesses = businesses.filter(b => b.flaggedForReview).length;
  const totalUsers = businesses.reduce((sum, b) => sum + (b.userCount || 0), 0);
  const totalRevenue = businesses.reduce((sum, b) => sum + (b.totalRevenue || 0), 0);
  const totalOrderRevenue = businesses.reduce((sum, b) => sum + (b.orderRevenue || 0), 0);
  const totalSaleRevenue = businesses.reduce((sum, b) => sum + (b.saleRevenue || 0), 0);
  const totalExpenses = businesses.reduce((sum, b) => sum + (b.totalExpenses || 0), 0);
  const totalNetProfit = businesses.reduce((sum, b) => sum + (b.netProfit || 0), 0);
  const totalProducts = businesses.reduce((sum, b) => sum + (b.productCount || 0), 0);
  const totalSalesCount = businesses.reduce((sum, b) => sum + (b.saleCount || 0), 0);

  const navBadges = { businesses: totalBusinesses, datadeletion: dataDeletionPendingCount, upgrades: upgradesPendingCount };

  return (
    <div className="ap-root">
      {sidebarOpen && <div className="ap-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* ── Sidebar ── */}
      <aside className={`ap-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="ap-sidebar-head">
          <h1>Admin Console</h1>
          <p>Platform analytics &amp; business operations</p>
        </div>
        <nav className="ap-nav">
          {navItems.map(item => (
            <button
              key={item.key}
              className={`ap-nav-item ${activeTab === item.key ? 'active' : ''}`}
              onClick={() => navigateToTab(item.key)}
            >
              <span className="ap-nav-icon">{item.icon}</span>
              {item.label}
              {!!navBadges[item.key] && <span className="ap-nav-badge">{navBadges[item.key]}</span>}
            </button>
          ))}
        </nav>
        <div className="ap-sidebar-foot">
          <div className="ap-theme-toggle">
            <span>{darkMode ? 'Dark mode' : 'Light mode'}</span>
            <button onClick={() => setDarkMode(d => !d)} title="Toggle theme">
              {darkMode ? '☀' : '☾'}
            </button>
          </div>
          <button className="ap-exit-link" onClick={() => {
            if (onExit) return onExit();
            api.clearToken();
            window.location.href = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/auth/google`;
          }}>
            {onExit ? 'Sign out' : '← Back to your business'}
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="ap-main">
        <div className="ap-mobile-bar">
          <button onClick={() => setSidebarOpen(true)}>☰</button>
          <strong>Admin Console</strong>
        </div>

        <div className="ap-content">
          {federationRetryId && (
            <div role="alert" style={{ padding: 16, border: '1px solid currentColor', marginBottom: 16 }}>
              ERP deletion completed, but Federation cleanup is pending.
              <button type="button" disabled={retryingFederation} onClick={async () => {
                setRetryingFederation(true);
                try {
                  await api.adminRetryFederationPurge(federationRetryId);
                  setFederationRetryId(null);
                  showAlert('Federation business cleanup completed.', 'success');
                } catch (e) {
                  showAlert('Federation cleanup retry failed: ' + e.message, 'error');
                } finally {
                  setRetryingFederation(false);
                }
              }}>{retryingFederation ? 'Retrying…' : 'Retry Federation cleanup'}</button>
            </div>
          )}
          {activeTab === 'overview' && (
            <>
              <div className="ap-page-head">
                <div>
                  <h2>Platform Overview</h2>
                  <div className="ap-sub">A snapshot of every business on Wavelines, right now.</div>
                </div>
                <div className="ap-controls">
                  <select className="ap-select" value={timeRange} onChange={e => setTimeRange(e.target.value)}>
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                    <option value="365d">Last year</option>
                  </select>
                  <button className="ap-refresh-btn" onClick={loadAdminData}>↻ Refresh</button>
                </div>
              </div>

              <div className="ap-stat-grid">
                <Stat label="Total Revenue" value={fmt(totalRevenue)} color="#e94560" hero />
                <Stat label="Net Profit" value={fmt(totalNetProfit)} color={totalNetProfit >= 0 ? '#10B981' : '#EF4444'} />
                <Stat label="Total Businesses" value={totalBusinesses} color="#3B82F6" />
                <Stat label="Active" value={activeBusinesses} color="#10B981" />
                <Stat label="Suspended" value={suspendedBusinesses} color="#EF4444" />
                <Stat label="Flagged for Review" value={flaggedBusinesses} color="#F59E0B" />
                <Stat label="Total Users" value={totalUsers} color="#8B5CF6" />
                <Stat label="Total Products" value={totalProducts} color="#F59E0B" />
                <Stat label="Order Revenue" value={fmt(totalOrderRevenue)} color="#3B82F6" />
                <Stat label="Walk-in Sales Revenue" value={fmt(totalSaleRevenue)} color="#06B6D4" />
                <Stat label="Walk-in Sales Count" value={totalSalesCount} color="#06B6D4" />
                <Stat label="Total Expenses" value={fmt(totalExpenses)} color="#F59E0B" />
              </div>

              <OverviewPanels
                businesses={businesses}
                chartData={chartData}
                onBusinessSelect={handleBusinessSelect}
              />
            </>
          )}

          {activeTab === 'businesses' && (
            <>
              <div className="ap-page-head">
                <div>
                  <h2>Businesses</h2>
                  <div className="ap-sub">{filteredBusinesses.length} of {totalBusinesses} businesses</div>
                </div>
                <div className="ap-controls">
                  <input
                    className="ap-search"
                    placeholder="Search by name or owner email…"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                  <button
                    className="ap-icon-btn"
                    style={onlyFlagged ? { background: 'rgba(245,158,11,.15)', color: 'var(--ap-warn)' } : undefined}
                    title="Show only businesses flagged for signup review"
                    onClick={() => setOnlyFlagged(v => !v)}
                  >
                    ⚠ Flagged {flaggedBusinesses > 0 ? `(${flaggedBusinesses})` : ''}
                  </button>
                  <ExportButton
                    rows={filteredBusinesses.map(b => ({
                      Name: b.name, Owner: b.ownerEmail, Plan: b.plan,
                      Users: b.userCount || 0, Products: b.productCount || 0,
                      Orders: b.orderCount || 0, 'Order Revenue': b.orderRevenue || 0,
                      'Walk-in Sales': b.saleCount || 0, 'Sales Revenue': b.saleRevenue || 0,
                      'Total Revenue': b.totalRevenue || 0, Expenses: b.totalExpenses || 0,
                      'Net Profit': b.netProfit || 0,
                      Created: b.createdAt ? new Date(b.createdAt).toLocaleDateString() : ''
                    }))}
                    filename="wavelines-businesses"
                  />
                </div>
              </div>
              <BusinessTable
                businesses={filteredBusinesses}
                sortField={sortField}
                sortDirection={sortDirection}
                handleSort={handleSort}
                onBusinessSelect={handleBusinessSelect}
                onSetPlan={handleSetPlan}
                onClearFlag={handleClearFlag}
                onDeleteBusiness={openDeleteModal}
                planUpdating={planUpdating}
              />
            </>
          )}

          {activeTab === 'analytics' && (
            <AnalyticsTab businesses={businesses} chartData={chartData} timeRange={timeRange} setTimeRange={setTimeRange} />
          )}

          {!hideAdminManagement && activeTab === 'admins' && (
            <AdminsTab showAlert={showAlert} />
          )}

          {activeTab === 'upgrades' && (
            <SubscriptionBillingTab showAlert={showAlert} onPendingCountChange={setUpgradesPendingCount} />
          )}

          {activeTab === 'datadeletion' && (
            <DataDeletionTab showAlert={showAlert} onPendingCountChange={setDataDeletionPendingCount} />
          )}

          {activeTab === 'outbox' && (
            <EmailOutboxTab showAlert={showAlert} />
          )}


          {activeTab === 'health' && (
            <SystemHealthTab showAlert={showAlert} />
          )}

          {activeTab === 'auditlog' && (
            <AuditLogTab showAlert={showAlert} />
          )}
        </div>
      </div>

      {selectedBusiness && businessDetail && (
        <BusinessDetailModal
          business={selectedBusiness}
          detail={businessDetail}
          onClose={handleCloseBusinessDetail}
          onSetPlan={handleSetPlan}
          onSetSubscription={handleSetSubscription}
          onDeleteBusiness={openDeleteModal}
          planUpdating={planUpdating}
        />
      )}

      {deleteTarget && (
        <DeleteBusinessModal
          business={deleteTarget}
          deleteInput={deleteInput}
          setDeleteInput={setDeleteInput}
          deleting={deleting}
          onConfirm={confirmDeleteBusiness}
          onCancel={() => { setDeleteTarget(null); setDeleteInput(''); }}
        />
      )}
    </div>
  );
}

// ── Overview: chart + top businesses ─────────────────────────────
function OverviewPanels({ businesses, chartData, onBusinessSelect }) {
  const topBusinesses = [...businesses].sort((a, b) => (b.totalRevenue || 0) - (a.totalRevenue || 0)).slice(0, 5);
  const rankClass = (i) => (i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : '');

  return (
    <>
      <div className="ap-panel">
        <div className="ap-panel-head">
          <h3>Platform Revenue</h3>
          <ExportButton
            rows={(chartData?.revenueData || []).map(d => ({ Date: d.date, Revenue: d.revenue, Orders: d.orders }))}
            filename="wavelines-revenue-trend"
          />
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={chartData?.revenueData || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--ap-border)" />
            <XAxis dataKey="date" stroke="var(--ap-text-faint)" fontSize={11} />
            <YAxis stroke="var(--ap-text-faint)" fontSize={11} />
            <Tooltip
              formatter={(value, name) => [name === 'orders' ? value : fmt(value), name === 'orders' ? 'Orders' : 'Revenue']}
              contentStyle={{ background: 'var(--ap-surface)', border: '1px solid var(--ap-border)', borderRadius: 8, fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="revenue" fill="#10B981" name="Revenue" radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="orders" stroke="#e94560" name="Orders" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="ap-panel">
        <div className="ap-panel-head">
          <h3>Top 5 Businesses</h3>
        </div>
        {topBusinesses.length === 0 ? (
          <div className="ap-empty">
            <div className="ap-empty-icon">◇</div>
            No business revenue recorded yet.
          </div>
        ) : (
          topBusinesses.map((b, i) => (
            <div key={b._id} className="ap-rank-row" onClick={() => onBusinessSelect(b)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className={`ap-rank-num ${rankClass(i)}`}>{i + 1}</span>
                <div>
                  <div className="ap-rank-name">{b.name}</div>
                  <div className="ap-rank-meta">{b.plan} · {b.userCount || 0} users</div>
                </div>
              </div>
              <div>
                <div className="ap-rank-value">{fmt(b.totalRevenue || 0)}</div>
                <div className="ap-rank-sub">{b.productCount || 0} products</div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}

// ── Businesses table ──────────────────────────────────────────────
function BusinessTable({ businesses, sortField, sortDirection, handleSort, onBusinessSelect, onSetPlan, onClearFlag, onDeleteBusiness, planUpdating }) {
  const arrow = (field) => (sortField === field ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : '');
  const planClass = (plan) => (plan === 'active' ? 'active' : plan === 'suspended' ? 'suspended' : 'trial');

  return (
    <div className="ap-panel" style={{ padding: 0 }}>
      <div className="ap-table-wrap">
        <table className="ap-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')}>Business{arrow('name')}</th>
              <th onClick={() => handleSort('plan')}>Plan{arrow('plan')}</th>
              <th onClick={() => handleSort('userCount')}>Users{arrow('userCount')}</th>
              <th onClick={() => handleSort('productCount')}>Products{arrow('productCount')}</th>
              <th onClick={() => handleSort('totalRevenue')}>Revenue{arrow('totalRevenue')}</th>
              <th onClick={() => handleSort('netProfit')}>Net Profit{arrow('netProfit')}</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {businesses.length === 0 ? (
              <tr><td colSpan={7}><div className="ap-empty">No businesses match your search.</div></td></tr>
            ) : businesses.map(b => {
              const isUpdating = planUpdating === b._id;
              const netProfit = b.netProfit || 0;
              return (
                <tr key={b._id}>
                  <td>
                    <div className="ap-biz-name" style={{ cursor: 'pointer' }} onClick={() => onBusinessSelect(b)}>
                      {b.name}
                      {b.flaggedForReview && (
                        <span
                          className="ap-badge suspended"
                          style={{ marginLeft: 6 }}
                          title={(b.flagReasons || []).join('; ')}
                        >⚠ Review</span>
                      )}
                    </div>
                    <div className="ap-biz-email">{b.ownerEmail}</div>
                  </td>
                  <td><span className={`ap-badge ${planClass(b.plan)}`}>{b.plan}</span></td>
                  <td>{b.userCount || 0}</td>
                  <td>{b.productCount || 0}</td>
                  <td style={{ fontWeight: 700, color: 'var(--ap-success)' }}>{fmt(b.totalRevenue || 0)}</td>
                  <td>
                    <span className={`ap-profit ${netProfit >= 0 ? 'positive' : 'negative'}`}>{fmt(netProfit)}</span>
                  </td>
                  <td>
                    <div className="ap-row-actions">
                      <button className="ap-icon-btn" title="View details" onClick={() => onBusinessSelect(b)}>👁</button>
                      {b.flaggedForReview && (
                        <button
                          className="ap-icon-btn success" title={`Clear review flag: ${(b.flagReasons || []).join('; ')}`} disabled={isUpdating}
                          onClick={() => onClearFlag(b)}
                        >✓</button>
                      )}
                      {b.plan === 'suspended' ? (
                        <button
                          className="ap-icon-btn success" title="Reactivate business" disabled={isUpdating}
                          onClick={() => onSetPlan(b, 'active')}
                        >▶</button>
                      ) : (
                        <button
                          className="ap-icon-btn warn" title="Suspend business" disabled={isUpdating}
                          onClick={() => onSetPlan(b, 'suspended')}
                        >⏸</button>
                      )}
                      <button
                        className="ap-icon-btn danger" title="Delete business permanently" disabled={isUpdating}
                        onClick={() => onDeleteBusiness(b)}
                      >🗑</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Analytics ────────────────────────────────────────────────────
function AnalyticsTab({ businesses, chartData, timeRange, setTimeRange }) {
  const planDistribution = [
    { name: 'Active', value: businesses.filter(b => b.plan === 'active').length },
    { name: 'Trial', value: businesses.filter(b => b.plan === 'trial').length },
    { name: 'Suspended', value: businesses.filter(b => b.plan === 'suspended').length },
  ];

  const roleDistribution = [
    { name: 'Managers', value: businesses.reduce((sum, b) => sum + (b.managerCount || 0), 0) },
    { name: 'Sales', value: businesses.reduce((sum, b) => sum + (b.salesCount || 0), 0) },
    { name: 'Cashiers', value: businesses.reduce((sum, b) => sum + (b.cashierCount || 0), 0) },
    { name: 'Ambassadors', value: businesses.reduce((sum, b) => sum + (b.ambassadorCount || 0), 0) },
  ];

  const topRevenue = [...businesses]
    .sort((a, b) => (b.totalRevenue || 0) - (a.totalRevenue || 0))
    .slice(0, 10)
    .map(b => ({ name: b.name, revenue: b.totalRevenue || 0 }));

  const revenueSource = [
    { name: 'Orders', value: businesses.reduce((sum, b) => sum + (b.orderRevenue || 0), 0) },
    { name: 'Walk-in Sales', value: businesses.reduce((sum, b) => sum + (b.saleRevenue || 0), 0) },
  ];

  const topByProfit = [...businesses]
    .map(b => ({ name: b.name, profit: b.netProfit || 0 }))
    .sort((a, b) => b.profit - a.profit)
    .slice(0, 10);

  const losingBusinesses = businesses.filter(b => {
    const np = b.netProfit || 0;
    return np < 0;
  });

  const g = chartData?.growthData || {};

  return (
    <>
      <div className="ap-page-head">
        <div>
          <h2>Advanced Analytics</h2>
          <div className="ap-sub">Distribution, growth, and revenue concentration across the platform.</div>
        </div>
        <div className="ap-controls">
          <select className="ap-select" value={timeRange} onChange={e => setTimeRange(e.target.value)}>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="365d">Last year</option>
          </select>
          <ExportButton
            rows={topRevenue.map(r => ({ Business: r.name, Revenue: r.revenue }))}
            filename="wavelines-top-revenue"
          />
        </div>
      </div>

      <div className="ap-stat-grid">
        <Stat label="Business Growth" value={`${g.businessGrowth > 0 ? '+' : ''}${g.businessGrowth || 0}%`} color="#10B981" />
        <Stat label="Revenue Growth" value={`${g.revenueGrowth > 0 ? '+' : ''}${g.revenueGrowth || 0}%`} color="#e94560" />
        <Stat label="User Growth" value={`${g.userGrowth > 0 ? '+' : ''}${g.userGrowth || 0}%`} color="#8B5CF6" />
        <Stat label="Product Growth" value={`${g.productGrowth > 0 ? '+' : ''}${g.productGrowth || 0}%`} color="#F59E0B" />
      </div>

      <div className="ap-panel-grid-2">
        <div className="ap-panel">
          <div className="ap-panel-head"><h3>Business Plan Distribution</h3></div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={planDistribution}
                cx="50%" cy="50%" labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={78} dataKey="value"
              >
                {planDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: 'var(--ap-surface)', border: '1px solid var(--ap-border)', borderRadius: 8, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="ap-panel">
          <div className="ap-panel-head"><h3>User Role Distribution</h3></div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={roleDistribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--ap-border)" />
              <XAxis dataKey="name" stroke="var(--ap-text-faint)" fontSize={11} />
              <YAxis stroke="var(--ap-text-faint)" fontSize={11} />
              <Tooltip contentStyle={{ background: 'var(--ap-surface)', border: '1px solid var(--ap-border)', borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="value" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="ap-panel">
        <div className="ap-panel-head"><h3>Top 10 Businesses by Revenue</h3></div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={topRevenue} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="var(--ap-border)" />
            <XAxis type="number" stroke="var(--ap-text-faint)" fontSize={11} />
            <YAxis type="category" dataKey="name" stroke="var(--ap-text-faint)" width={110} fontSize={11} />
            <Tooltip formatter={(value) => fmt(value)} contentStyle={{ background: 'var(--ap-surface)', border: '1px solid var(--ap-border)', borderRadius: 8, fontSize: 12 }} />
            <Bar dataKey="revenue" fill="#10B981" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="ap-panel-grid-2">
        <div className="ap-panel">
          <div className="ap-panel-head"><h3>Revenue Source Split</h3></div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={revenueSource}
                cx="50%" cy="50%" labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={78} dataKey="value"
              >
                <Cell fill="#3B82F6" />
                <Cell fill="#06B6D4" />
              </Pie>
              <Tooltip formatter={(value) => fmt(value)} contentStyle={{ background: 'var(--ap-surface)', border: '1px solid var(--ap-border)', borderRadius: 8, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="ap-panel">
          <div className="ap-panel-head">
            <h3>Top 10 by Net Profit</h3>
            <ExportButton
              rows={topByProfit.map(r => ({ Business: r.name, 'Net Profit': r.profit }))}
              filename="wavelines-top-profit"
            />
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={topByProfit} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--ap-border)" />
              <XAxis type="number" stroke="var(--ap-text-faint)" fontSize={11} />
              <YAxis type="category" dataKey="name" stroke="var(--ap-text-faint)" width={100} fontSize={10.5} />
              <Tooltip formatter={(value) => fmt(value)} contentStyle={{ background: 'var(--ap-surface)', border: '1px solid var(--ap-border)', borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="profit" radius={[0, 4, 4, 0]}>
                {topByProfit.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.profit >= 0 ? '#10B981' : '#EF4444'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {losingBusinesses.length > 0 && (
        <div className="ap-panel">
          <div className="ap-panel-head">
            <h3>⚠ Businesses Operating at a Loss ({losingBusinesses.length})</h3>
          </div>
          {losingBusinesses.map(b => {
            const np = b.netProfit || 0;
            return (
              <div key={b._id} className="ap-rank-row" style={{ cursor: 'default' }}>
                <div>
                  <div className="ap-rank-name">{b.name}</div>
                  <div className="ap-rank-meta">Revenue {fmt(b.totalRevenue || 0)} · Expenses {fmt(b.totalExpenses || 0)}</div>
                </div>
                <div className="ap-profit negative">{fmt(np)}</div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function DataDeletionTab({ showAlert, onPendingCountChange }) {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectId, setRejectId] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  // The approve action for a request sitting in this modal — separate
  // from busyId below so the confirmation checkbox always starts
  // unchecked for whichever request is currently open, never carried
  // over from a previously-approved one.
  const [approveId, setApproveId] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [actionSuccess, setActionSuccess] = useState('');

  const load = () => {
    setLoading(true);
    api.adminGetDataDeletionRequests(statusFilter)
      .then(r => {
        setRequests(r.requests || []);
        // pendingCount comes back regardless of statusFilter (server.js
        // always computes it fresh, not just when viewing the "pending"
        // filter) — see this component's own onPendingCountChange prop
        // comment in AdminPortal for why this is wired up at all.
        onPendingCountChange && onPendingCountChange(r.pendingCount || 0);
      })
      .catch(e => showAlert(e.message, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = async () => {
    const requestId = approveId;
    setBusyId(requestId);
    setActionSuccess('');
    try {
      await api.adminApproveDataDeletionRequest(requestId, confirmed);
      // The destructive action has already succeeded at this point. Reflect
      // that locally before any follow-up request so a refresh failure cannot
      // leave the confirmation modal looking stuck.
      setApproveId(null);
      setConfirmed(false);
      setRequests(current => statusFilter === 'pending'
        ? current.filter(request => request.requestId !== requestId)
        : current
      );
      onPendingCountChange && onPendingCountChange(current => Math.max(0, Number(current || 0) - 1));
      setActionSuccess('Business data permanently deleted successfully.');
      showAlert('Approved — all business data has been permanently deleted.', 'success');
      load();
    } catch (e) { showAlert(e.message, 'error'); }
    finally { setBusyId(null); }
  };

  const handleReject = async () => {
    const requestId = rejectId;
    setBusyId(requestId);
    setActionSuccess('');
    try {
      await api.adminRejectDataDeletionRequest(requestId, rejectNote);
      setRejectId(null);
      setRejectNote('');
      setRequests(current => statusFilter === 'pending'
        ? current.filter(request => request.requestId !== requestId)
        : current
      );
      onPendingCountChange && onPendingCountChange(current => Math.max(0, Number(current || 0) - 1));
      setActionSuccess('Data deletion request rejected successfully.');
      showAlert('Request rejected.', 'success');
      load();
    } catch (e) { showAlert(e.message, 'error'); }
    finally { setBusyId(null); }
  };

  return (
    <>
      <div className="ap-page-head">
        <div>
          <h2>Data Deletion Requests</h2>
          <div className="ap-sub">Nothing is deleted until you personally verify the request with the business owner and tick the confirmation below.</div>
        </div>
      </div>

      {actionSuccess && (
        <div className="ap-panel" role="status" style={{ marginBottom: 16, borderColor: 'var(--ap-success)' }}>
          <div style={{ color: 'var(--ap-success)', fontSize: 13, fontWeight: 700 }}>✓ {actionSuccess}</div>
        </div>
      )}

      <div className="ap-btn-row" style={{ marginBottom: 16 }}>
        <div style={{ flex: 1 }} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ fontSize: 12.5, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--ap-border)', background: 'var(--ap-surface-2)', color: 'var(--ap-text)' }}>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
      </div>

      {rejectId && (
        <div className="ap-modal-backdrop" onClick={() => setRejectId(null)}>
          <div className="ap-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, fontSize: 14 }}>Reject this request</h3>
            <textarea
              value={rejectNote}
              onChange={e => setRejectNote(e.target.value)}
              placeholder="Optional reason — shown to the business"
              rows={3}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--ap-border)', borderRadius: 8, fontSize: 13, outline: 'none', resize: 'vertical', marginBottom: 14, background: 'var(--ap-surface-2)', color: 'var(--ap-text)' }}
            />
            <div className="ap-btn-row">
              <button className="ap-btn ap-btn-reject" style={{ flex: 1 }} disabled={busyId === rejectId} onClick={handleReject}>Reject</button>
              <button className="ap-btn ap-btn-ghost" style={{ flex: 1 }} onClick={() => { setRejectId(null); setRejectNote(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {approveId && (
        <div className="ap-modal-backdrop" onClick={() => { setApproveId(null); setConfirmed(false); }}>
          <div className="ap-modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, fontSize: 14 }}>⚠️ Approve & Permanently Delete</h3>
            <p style={{ fontSize: 12.5, color: 'var(--ap-text-2)', marginBottom: 14 }}>
              This permanently deletes every order, payment, expense, inventory record, and related data for this business. User accounts are not affected. This cannot be undone.
            </p>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, marginBottom: 16, cursor: 'pointer' }}>
              <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
              I have personally verified this request with the business owner (a call, a reply on the usual channel, etc.) and confirm it's genuine.
            </label>
            <div className="ap-btn-row">
              <button className="ap-btn ap-btn-reject" style={{ flex: 1 }} disabled={!confirmed || busyId === approveId} onClick={handleApprove}>
                {busyId === approveId ? 'Deleting…' : 'Approve & Permanently Delete'}
              </button>
              <button className="ap-btn ap-btn-ghost" style={{ flex: 1 }} onClick={() => { setApproveId(null); setConfirmed(false); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="ap-panel"><div className="ap-empty">Loading…</div></div>
      ) : requests.length === 0 ? (
        <div className="ap-panel">
          <div className="ap-empty">
            <div className="ap-empty-icon">🗑️</div>
            No {statusFilter !== 'all' ? statusFilter : ''} data deletion requests.
          </div>
        </div>
      ) : (
        requests.map(r => (
          <div key={r.requestId} className="ap-request-card">
            <div className="ap-request-row">
              <div style={{ flex: 1, minWidth: 200 }}>
                <div className="ap-request-name">{r.businessName}</div>
                <div className="ap-request-meta">
                  Requested by {r.requestedBy}{r.businessOwnerEmail ? ` · owner: ${r.businessOwnerEmail}` : ''}
                </div>
                <div className="ap-request-date">{new Date(r.requestedAt).toLocaleString()}</div>
                {r.status === 'rejected' && r.rejectionReason && <div className="ap-request-desc">Rejected: {r.rejectionReason}</div>}
                {r.status !== 'pending' && (
                  <div className="ap-request-date">{r.status === 'approved' ? '✓ Approved & deleted' : '✕ Rejected'} by {r.resolvedBy} on {new Date(r.resolvedAt).toLocaleDateString()}</div>
                )}
              </div>
              {r.status === 'pending' && (
                <div className="ap-btn-row">
                  <button className="ap-btn ap-btn-approve" disabled={busyId === r.requestId} onClick={() => { setApproveId(r.requestId); setConfirmed(false); }}>Approve</button>
                  <button className="ap-btn ap-btn-reject" disabled={busyId === r.requestId} onClick={() => { setRejectId(r.requestId); setRejectNote(''); }}>Reject</button>
                </div>
              )}
            </div>
          </div>
        ))
      )}
    </>
  );
}

function BusinessDetailModal({ business, detail, onClose, onSetPlan, onSetSubscription, onDeleteBusiness, planUpdating }) {
  const s = detail?.summary || {};
  const orderRevenue = s.orderRevenue ?? business.orderRevenue ?? 0;
  const saleRevenue = s.saleRevenue ?? business.saleRevenue ?? 0;
  const totalRevenue = s.totalRevenue ?? business.totalRevenue ?? 0;
  const totalExpenses = s.totalExpenses ?? business.totalExpenses ?? 0;
  const netProfit = s.netProfit ?? business.netProfit ?? 0;
  const isUpdating = planUpdating === business._id;
  const [tierDraft, setTierDraft] = useState(business.subscriptionTier || 'trial');
  const [cycleDraft, setCycleDraft] = useState(business.billingCycle || 'monthly');
  const tierChanged = tierDraft !== (business.subscriptionTier || 'trial') || cycleDraft !== (business.billingCycle || 'monthly');

  return (
    <div className="ap-modal-backdrop" onClick={onClose}>
      <div className="ap-modal" onClick={e => e.stopPropagation()}>
        <div className="ap-modal-head">
          <div>
            <h2>{business.name}</h2>
            <div style={{ fontSize: 12, color: 'var(--ap-text-faint)' }}>
              {business.ownerEmail} · <span className={`ap-badge ${business.plan === 'active' ? 'active' : business.plan === 'suspended' ? 'suspended' : 'trial'}`}>{business.plan}</span>
            </div>
          </div>
          <button className="ap-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="ap-stat-grid" style={{ marginBottom: 20 }}>
          <Stat label="Total Revenue" value={fmt(totalRevenue)} color="#e94560" hero />
          <Stat label="Net Profit" value={fmt(netProfit)} color={netProfit >= 0 ? '#10B981' : '#EF4444'} />
          <Stat label="Order Revenue" value={fmt(orderRevenue)} color="#3B82F6" />
          <Stat label="Walk-in Sales Revenue" value={fmt(saleRevenue)} color="#06B6D4" />
          <Stat label="Expenses" value={fmt(totalExpenses)} color="#F59E0B" />
          <Stat label="Users" value={business.userCount || 0} color="#8B5CF6" />
          <Stat label="Products" value={business.productCount || 0} color="#F59E0B" />
          <Stat label="Orders" value={s.orderCount ?? business.orderCount ?? 0} color="#3B82F6" />
          <Stat label="Walk-in Sales" value={s.saleCount ?? business.saleCount ?? 0} color="#06B6D4" />
        </div>

        {detail && (
          <div className="ap-panel" style={{ marginBottom: 16 }}>
            <div className="ap-panel-head"><h3>Business details</h3></div>
            <div className="ap-detail-grid">
              <div>Created <b>{new Date(business.createdAt).toLocaleDateString()}</b></div>
              <div>Plan <b>{business.plan}</b></div>
              <div>Location <b>{business.marketplaceProfile?.location || 'Not set'}</b></div>
              <div>WhatsApp <b>{business.marketplaceProfile?.whatsapp || 'Not set'}</b></div>
              <div>Hub status <b>{business.hubStatus || 'inactive'}</b></div>
              <div>Total products <b>{business.productCount || 0}</b></div>
            </div>
          </div>
        )}

        {/* ── Subscription tier (Tier 10) — a separate axis from the
            account-status controls below. No self-serve payment yet, so
            this is a manual admin assignment after a business
            pays/upgrades outside the app. */}
        <div className="ap-panel" style={{ marginBottom: 16 }}>
          <div className="ap-panel-head"><h3>Subscription Plan</h3></div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ fontSize: 11.5, color: 'var(--ap-text-dim)', display: 'block', marginBottom: 4 }}>Tier</label>
              <select className="ap-select" value={tierDraft} onChange={e => setTierDraft(e.target.value)}>
                <option value="trial">Trial (free)</option>
                <option value="starter">Starter — ₦3,000/mo</option>
                <option value="growth">Growth — ₦10,000/mo</option>
                <option value="enterprise">Enterprise — ₦12,000/mo</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11.5, color: 'var(--ap-text-dim)', display: 'block', marginBottom: 4 }}>Billing cycle</label>
              <select className="ap-select" value={cycleDraft} onChange={e => setCycleDraft(e.target.value)} disabled={tierDraft === 'trial'}>
                <option value="monthly">Monthly</option>
                <option value="annual">Annual (20% off)</option>
              </select>
            </div>
            <button
              className="ap-btn ap-btn-approve ap-btn-sm"
              disabled={!tierChanged || isUpdating}
              onClick={() => onSetSubscription(business, tierDraft, cycleDraft)}
            >
              {isUpdating ? 'Saving…' : 'Save Plan'}
            </button>
          </div>
        </div>

        <div className="ap-btn-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div className="ap-btn-row">
            {business.plan === 'suspended' ? (
              <button className="ap-btn ap-btn-activate" disabled={isUpdating} onClick={() => onSetPlan(business, 'active')}>
                ▶ Reactivate
              </button>
            ) : (
              <button className="ap-btn ap-btn-suspend" disabled={isUpdating} onClick={() => onSetPlan(business, 'suspended')}>
                ⏸ Suspend
              </button>
            )}
            <button className="ap-btn ap-btn-delete" disabled={isUpdating} onClick={() => onDeleteBusiness(business)}>
              🗑 Delete
            </button>
          </div>
          <button className="ap-btn ap-btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Delete business confirmation modal ───────────────────────────
function DeleteBusinessModal({ business, deleteInput, setDeleteInput, deleting, onConfirm, onCancel }) {
  const matches = deleteInput === business.name;
  return (
    <div className="ap-modal-backdrop" onClick={onCancel}>
      <div className="ap-modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="ap-modal-head">
          <h2 style={{ color: 'var(--ap-danger)' }}>Delete business</h2>
          <button className="ap-modal-close" onClick={onCancel}>✕</button>
        </div>
        <div className="ap-danger-zone">
          <h4>This action is permanent</h4>
          <p>
            Deleting <b>{business.name}</b> removes its orders, payments, expenses, inventory, sales,
            users, and marketplace listings. This cannot be undone.
          </p>
          <p>Type <b>{business.name}</b> below to confirm:</p>
          <input
            className="ap-confirm-input"
            value={deleteInput}
            onChange={e => setDeleteInput(e.target.value)}
            placeholder={business.name}
            autoFocus
          />
          <div className="ap-btn-row">
            <button className="ap-btn ap-btn-delete" style={{ flex: 1 }} disabled={!matches || deleting} onClick={onConfirm}>
              {deleting ? 'Deleting…' : '🗑 Delete permanently'}
            </button>
            <button className="ap-btn ap-btn-ghost" style={{ flex: 1 }} onClick={onCancel}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminPortal;

// ══════════════════════════════════════════════════════════════
// ADMINS TAB — Tier 12 granular admin account management.
// Reachable by anyone who already has AdminPortal access (i.e. is in
// the ADMIN_EMAIL env list — see server.js's isPlatformAdmin) — that's
// exactly the superadmin audience the backend's requireAdminAccess
// gate on POST/PUT/DELETE /api/admin/admins already restricts this to.
// ══════════════════════════════════════════════════════════════
const AVAILABLE_PERMISSIONS = [
  { key: 'customer_care', label: 'Customer Care', hint: 'View and respond to buyer/business support inquiries' },
  { key: 'billing', label: 'Billing', hint: 'View and manage subscription/payment issues' },
  { key: 'business_management', label: 'Business Management', hint: 'View and manage native ERP businesses' },
  { key: 'email_outbox', label: 'Email Outbox Manager', hint: 'Review Wavelines account emails and handle pending messages when automatic delivery needs manual fallback' },
  { key: 'data_deletion', label: 'Data Deletion Reviewer', hint: 'Review and approve/reject business data deletion requests' },
];

function AdminsTab({ showAlert }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Same reasoning as revealedLink in the Federation tab — the link is
  // also drafted into the Email Outbox, but showing it here is the
  // quickest way to get a just-added admin their setup link.
  const [revealedAdminLink, setRevealedAdminLink] = useState(null); // { email, url }
  const [adminLinkCopied, setAdminLinkCopied] = useState(false);
  const copyAdminLink = (url) => {
    navigator.clipboard.writeText(url);
    setAdminLinkCopied(true);
    setTimeout(() => setAdminLinkCopied(false), 2000);
  };

  const load = () => api.adminListAdmins().then(setAdmins).catch(e => showAlert(e.message, 'error')).finally(() => setLoading(false));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (admin) => {
    if (!window.confirm(`Remove admin access for ${admin.email}?`)) return;
    try {
      await api.adminDeleteAdmin(admin._id);
      showAlert(`Removed ${admin.email}.`, 'success');
      load();
    } catch (e) { showAlert(e.message, 'error'); }
  };

  const handleToggleActive = async (admin) => {
    try {
      await api.adminUpdateAdmin(admin._id, { active: !admin.active });
      load();
    } catch (e) { showAlert(e.message, 'error'); }
  };

  const handleCreateSetupLink = async (admin) => {
    try {
      const result = await api.adminCreateAdminSetupLink(admin._id);
      setRevealedAdminLink({ email: result.email, url: result.setupUrl });
      showAlert(result.message, 'success');
    } catch (e) { showAlert(e.message, 'error'); }
  };

  if (loading) return <div className="spinner">Loading admins...</div>;

  return (
    <div className="card">
      <div className="section-header">
        <h2>☺ Platform Admins ({admins.length})</h2>
        <button className="ap-btn ap-btn-primary" onClick={() => { setShowAddForm(v => !v); setEditingId(null); }}>
          {showAddForm ? '✕ Cancel' : '+ Add Admin'}
        </button>
      </div>

      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '4px 0 16px' }}>
        This list is separate from the ADMIN_EMAIL environment variable, which always retains full, unrestricted access regardless of what's shown here. Admins added below get exactly the permissions you assign — no more.
      </p>

      {revealedAdminLink && (
        <div className="ap-key-reveal" style={{ marginBottom: 16 }}>
          <div className="ap-key-reveal-warn" style={{ background: 'var(--ap-info-bg, rgba(59,130,246,.12))', color: 'var(--ap-info, #3b82f6)' }}>
            ✉ Setup link for {revealedAdminLink.email} — if your email service isn't fully set up yet, this link won't have reached them automatically.
          </div>
          <div className="ap-key-row">
            <div className="ap-key-value" style={{ wordBreak: 'break-all' }}>{revealedAdminLink.url}</div>
            <button className={`ap-copy-btn ${adminLinkCopied ? 'copied' : ''}`} onClick={() => copyAdminLink(revealedAdminLink.url)}>
              {adminLinkCopied ? '✓ Copied' : 'Copy'}
            </button>
            <button className="ap-btn ap-btn-ghost ap-btn-sm" onClick={() => setRevealedAdminLink(null)}>Dismiss</button>
          </div>
        </div>
      )}

      {showAddForm && (
        <AddAdminForm
          onDone={(setupUrl, email) => {
            setShowAddForm(false);
            load();
            if (setupUrl) setRevealedAdminLink({ email, url: setupUrl });
          }}
          showAlert={showAlert}
        />
      )}

      <div className="table-wrap">
        <table className="w-full">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Permissions</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>
                No granular admins added yet.
              </td></tr>
            )}
            {admins.map(admin => (
              <React.Fragment key={admin._id}>
                <tr>
                  <td>{admin.email}</td>
                  <td>{admin.roleLabel}{admin.isSuperAdmin && <span className="ap-badge active" style={{ marginLeft: 6 }}>Superadmin</span>}</td>
                  <td style={{ fontSize: 12 }}>
                    {admin.isSuperAdmin ? 'All permissions' : (admin.permissions.length ? admin.permissions.map(p => AVAILABLE_PERMISSIONS.find(a => a.key === p)?.label || p).join(', ') : '—')}
                  </td>
                  <td>
                    <span className={`ap-badge ${admin.active ? 'active' : 'suspended'}`}>{admin.active ? 'Active' : 'Deactivated'}</span>
                    {admin.passwordHash === undefined && null}
                    {!admin.setupToken && !admin.active ? null : null}
                  </td>
                  <td>
                    <div className="ap-row-actions">
                      {admin.setupPending && <button className="ap-icon-btn" title="Create a replacement password-setup link" onClick={() => handleCreateSetupLink(admin)}>Setup</button>}
                      <button className="ap-icon-btn" title={admin.active ? 'Deactivate' : 'Reactivate'} onClick={() => handleToggleActive(admin)}>
                        {admin.active ? '⏸' : '▶'}
                      </button>
                      <button className="ap-icon-btn" title="Edit permissions" onClick={() => setEditingId(editingId === admin._id ? null : admin._id)}>✎</button>
                      <button className="ap-icon-btn danger" title="Remove" onClick={() => handleDelete(admin)}>🗑</button>
                    </div>
                  </td>
                </tr>
                {editingId === admin._id && (
                  <tr>
                    <td colSpan={5}>
                      <EditAdminForm admin={admin} onDone={() => { setEditingId(null); load(); }} showAlert={showAlert} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AddAdminForm({ onDone, showAlert }) {
  const [email, setEmail] = useState('');
  const [roleLabel, setRoleLabel] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [saving, setSaving] = useState(false);

  const togglePerm = (key) => setPermissions(p => p.includes(key) ? p.filter(x => x !== key) : [...p, key]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSaving(true);
    try {
      const res = await api.adminCreateAdmin({ email: email.trim(), roleLabel: roleLabel.trim() || 'Admin', permissions, isSuperAdmin });
      showAlert(res.message, 'success');
      onDone(res.setupUrl, email.trim());
    } catch (e) { showAlert(e.message, 'error'); setSaving(false); }
  };

  return (
    <form onSubmit={submit} style={{ background: 'var(--surface2)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <input type="email" required placeholder="admin@example.com" value={email} onChange={e => setEmail(e.target.value)}
          style={{ flex: '1 1 220px', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }} />
        <input type="text" placeholder="Role label (e.g. Hub Review Manager)" value={roleLabel} onChange={e => setRoleLabel(e.target.value)}
          style={{ flex: '1 1 220px', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }} />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
          <input type="checkbox" checked={isSuperAdmin} onChange={e => setIsSuperAdmin(e.target.checked)} />
          Grant full superadmin access (bypasses individual permissions below)
        </label>
        {!isSuperAdmin && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {AVAILABLE_PERMISSIONS.map(p => (
              <label key={p.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5 }}>
                <input type="checkbox" checked={permissions.includes(p.key)} onChange={() => togglePerm(p.key)} style={{ marginTop: 2 }} />
                <span><strong>{p.label}</strong> — <span style={{ color: 'var(--text-muted)' }}>{p.hint}</span></span>
              </label>
            ))}
          </div>
        )}
      </div>

      <button type="submit" className="ap-btn ap-btn-primary" disabled={saving}>
        {saving ? 'Sending invite…' : '✉ Send setup invite'}
      </button>
      <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
        They'll get an email with a one-time link to set their own password — you never see or handle it.
      </p>
    </form>
  );
}

function EditAdminForm({ admin, onDone, showAlert }) {
  const [roleLabel, setRoleLabel] = useState(admin.roleLabel);
  const [permissions, setPermissions] = useState(admin.permissions || []);
  const [isSuperAdmin, setIsSuperAdmin] = useState(admin.isSuperAdmin);
  const [saving, setSaving] = useState(false);

  const togglePerm = (key) => setPermissions(p => p.includes(key) ? p.filter(x => x !== key) : [...p, key]);

  const save = async () => {
    setSaving(true);
    try {
      await api.adminUpdateAdmin(admin._id, { roleLabel, permissions, isSuperAdmin });
      showAlert(`Updated ${admin.email}.`, 'success');
      onDone();
    } catch (e) { showAlert(e.message, 'error'); setSaving(false); }
  };

  return (
    <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: 16 }}>
      <input type="text" value={roleLabel} onChange={e => setRoleLabel(e.target.value)} placeholder="Role label"
        style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, marginBottom: 10 }} />
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
        <input type="checkbox" checked={isSuperAdmin} onChange={e => setIsSuperAdmin(e.target.checked)} />
        Full superadmin access
      </label>
      {!isSuperAdmin && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          {AVAILABLE_PERMISSIONS.map(p => (
            <label key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
              <input type="checkbox" checked={permissions.includes(p.key)} onChange={() => togglePerm(p.key)} />
              {p.label}
            </label>
          ))}
        </div>
      )}
      <button className="ap-btn ap-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// SYSTEM HEALTH TAB — Cloudinary usage + internal AI/email call
// counters + basic server stats (see GET /api/admin/system-health).
// ══════════════════════════════════════════════════════════════
function EmailOutboxTab({ showAlert }) {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = () => {
    setLoading(true);
    api.adminGetOutbox(statusFilter)
      .then(r => setEmails(r.emails || []))
      .catch(e => showAlert(e.message, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyBody = (entry) => {
    navigator.clipboard.writeText(entry.text || entry.html || '');
    setCopiedId(entry._id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const doMarkSent = async (id) => {
    setBusyId(id);
    try {
      await api.adminMarkOutboxSent(id);
      showAlert('Marked as sent.', 'success');
      load();
    } catch (e) { showAlert(e.message, 'error'); }
    setBusyId(null);
  };

  const doDismiss = async (id) => {
    setBusyId(id);
    try {
      await api.adminDismissOutboxEntry(id);
      showAlert('Draft dismissed.', 'success');
      load();
    } catch (e) { showAlert(e.message, 'error'); }
    setBusyId(null);
  };

  return (
    <>
      <div className="ap-page-head">
        <div>
          <h2>Email Outbox</h2>
          <div className="ap-sub">Wavelines account and billing emails are delivered automatically through the shared Resend email service. Anything still pending here is waiting for retry or can be sent manually as a fallback: copy the body into your email client, then mark it sent.</div>
        </div>
      </div>

      <div className="ap-btn-row" style={{ marginBottom: 16 }}>
        <div style={{ flex: 1 }} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ fontSize: 12.5, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--ap-border)', background: 'var(--ap-surface-2)', color: 'var(--ap-text)' }}>
          <option value="pending">Pending</option>
          <option value="sent">Sent</option>
          <option value="dismissed">Dismissed</option>
          <option value="all">All</option>
        </select>
      </div>

      {loading ? (
        <div className="ap-panel"><div className="ap-empty">Loading…</div></div>
      ) : emails.length === 0 ? (
        <div className="ap-panel">
          <div className="ap-empty">
            <div className="ap-empty-icon">✎</div>
            No {statusFilter !== 'all' ? statusFilter : ''} email records.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {emails.map(entry => (
            <div key={entry._id} className="ap-panel" style={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700 }}>{entry.subject}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--ap-text-muted)', marginTop: 2 }}>
                    To: {entry.to}{entry.businessName ? ` · ${entry.businessName}` : ''}
                    {entry.tags?.length ? ` · ${entry.tags.join(', ')}` : ''}<div>Delivery: {entry.deliveryState || entry.status}{entry.providerMessageId ? ` · ${entry.providerMessageId}` : ''}{entry.lastError ? ` · ${entry.lastError}` : ''}</div>
                  </div>
                </div>
              </div>
              <button className="ap-btn ap-btn-ghost" style={{ marginTop: 8, fontSize: 12 }} onClick={() => setExpandedId(expandedId === entry._id ? null : entry._id)}>
                {expandedId === entry._id ? 'Hide body' : 'Show body'}
              </button>
              {expandedId === entry._id && (
                <p style={{ fontSize: 12.5, whiteSpace: 'pre-wrap', background: 'var(--ap-surface-2)', borderRadius: 8, padding: 10, margin: '8px 0' }}>{entry.text || entry.html}</p>
              )}
              {statusFilter === 'pending' && (
                <div className="ap-btn-row" style={{ marginTop: 8 }}>
                  <button className="ap-btn ap-btn-ghost" onClick={() => copyBody(entry)}>{copiedId === entry._id ? '✓ Copied' : '📋 Copy body'}</button>
                  <button className="ap-btn ap-btn-approve" disabled={busyId === entry._id || !['manual','failed','review'].includes(entry.deliveryState)} onClick={() => doMarkSent(entry._id)}>{busyId === entry._id ? '…' : '✓ Mark sent'}</button>
                  <button className="ap-btn ap-btn-ghost" disabled={busyId === entry._id || !['manual','failed','review'].includes(entry.deliveryState)} onClick={() => doDismiss(entry._id)}>Dismiss</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function readableAuditAction(entry) {
  const path = entry.path || '';
  const method = entry.method || '';
  const rules = [
    [/\/upgrade-requests\/[^/]+\/approve$/, 'Approved a business plan payment'],
    [/\/upgrade-requests\/[^/]+\/reject$/, 'Rejected a business plan payment'],
    [/\/business\/[^/]+\/subscription$/, 'Changed a business subscription'],
    [/\/business\/[^/]+\/reactivate$/, 'Reactivated a business'],
    [/\/data-deletion-requests\/[^/]+\/approve$/, 'Approved a business data deletion request'],
    [/\/data-deletion-requests\/[^/]+\/reject$/, 'Rejected a business data deletion request'],
  ];
  const match = rules.find(([pattern]) => pattern.test(path));
  if (match) return match[1];
  if (entry.action && !/^(GET|POST|PUT|PATCH|DELETE)\s/.test(entry.action)) return entry.action;
  const clean = path.replace(/^\/api\/admin(?:-portal|-auth)?\/?/, '').replace(/[a-f0-9]{20,}|\d{10,}/gi, 'record');
  const words = clean.split(/[/-]+/).filter(Boolean).join(' ');
  const verb = ({ POST: 'Created', PUT: 'Updated', PATCH: 'Updated', DELETE: 'Deleted' })[method] || 'Performed';
  return `${verb} ${words || 'admin action'}`.replace(/\b\w/g, c => c.toUpperCase());
}

function readableAuditStatus(entry) {
  const code = Number(entry.statusCode || 0);
  if (entry.success && code === 201) return 'Created successfully';
  if (entry.success) return 'Completed successfully';
  if (code === 400) return 'Could not complete — invalid request';
  if (code === 401) return 'Not authenticated';
  if (code === 403) return 'Permission denied';
  if (code === 404) return 'Record not found';
  if (code === 409) return 'Blocked by a conflict or current state';
  if (code >= 500) return 'Server error';
  return 'Failed';
}

function AuditLogTab({ showAlert }) {
  const [entries, setEntries] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [adminFilter, setAdminFilter] = useState('');
  const [successFilter, setSuccessFilter] = useState('');
  const [actionSearch, setActionSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const perPage = 50;

  const load = () => {
    setLoading(true);
    const filters = { page, limit: perPage };
    if (adminFilter) filters.adminEmail = adminFilter;
    if (successFilter) filters.success = successFilter;
    if (actionSearch.trim()) filters.action = actionSearch.trim();
    api.adminGetAuditLog(filters)
      .then(r => { setEntries(r.entries || []); setTotal(r.total || 0); setAdmins(r.admins || []); })
      .catch(e => showAlert(e.message, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page, adminFilter, successFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const runSearch = (e) => { e.preventDefault(); setPage(1); load(); };

  return (
    <>
      <div className="ap-page-head">
        <div>
          <h2>Admin Audit Log</h2>
          <div className="ap-sub">Every action taken by every admin across the platform — logins, approvals, upgrades, business signups — logged automatically, no exceptions.</div>
        </div>
      </div>

      <form onSubmit={runSearch} className="ap-btn-row" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          value={actionSearch} onChange={e => setActionSearch(e.target.value)}
          placeholder="Search action (e.g. upgrade-requests, login)"
          style={{ fontSize: 12.5, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--ap-border)', background: 'var(--ap-surface-2)', color: 'var(--ap-text)', minWidth: 220 }}
        />
        <select value={adminFilter} onChange={e => { setAdminFilter(e.target.value); setPage(1); }} style={{ fontSize: 12.5, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--ap-border)', background: 'var(--ap-surface-2)', color: 'var(--ap-text)' }}>
          <option value="">All admins</option>
          {admins.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={successFilter} onChange={e => { setSuccessFilter(e.target.value); setPage(1); }} style={{ fontSize: 12.5, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--ap-border)', background: 'var(--ap-surface-2)', color: 'var(--ap-text)' }}>
          <option value="">Success + Failed</option>
          <option value="true">Success only</option>
          <option value="false">Failed only</option>
        </select>
        <button type="submit" className="ap-btn ap-btn-ghost">Search</button>
      </form>

      {loading ? (
        <div className="ap-panel"><div className="ap-empty">Loading…</div></div>
      ) : entries.length === 0 ? (
        <div className="ap-panel"><div className="ap-empty"><div className="ap-empty-icon">📜</div>No matching entries.</div></div>
      ) : (
        <div className="ap-panel" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--ap-border)' }}>
                <th style={{ padding: '10px 14px' }}>When</th>
                <th style={{ padding: '10px 14px' }}>Admin</th>
                <th style={{ padding: '10px 14px' }}>Action</th>
                <th style={{ padding: '10px 14px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <React.Fragment key={e._id}>
                  <tr
                    style={{ borderBottom: '1px solid var(--ap-border)', cursor: 'pointer' }}
                    onClick={() => setExpandedId(expandedId === e._id ? null : e._id)}
                  >
                    <td style={{ padding: '10px 14px', color: 'var(--ap-text-muted)', whiteSpace: 'nowrap' }}>{new Date(e.timestamp).toLocaleString()}</td>
                    <td style={{ padding: '10px 14px' }}>{e.adminEmail}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 650 }}>{readableAuditAction(e)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: e.success ? 'rgba(16,185,129,.12)' : 'rgba(239,68,68,.12)', color: e.success ? '#10b981' : '#ef4444' }}>
                        {readableAuditStatus(e)}
                      </span>
                    </td>
                  </tr>
                  {expandedId === e._id && (
                    <tr style={{ borderBottom: '1px solid var(--ap-border)' }}>
                      <td colSpan={4} style={{ padding: 14, background: 'var(--ap-surface-2)' }}>
                        <div style={{ border: '1px solid var(--ap-border)', background: 'var(--ap-surface)', borderRadius: 12, padding: 14 }}>
                          <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>{readableAuditAction(e)}</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, fontSize: 12 }}>
                            <div><span style={{ color: 'var(--ap-text-faint)' }}>Result</span><br/><strong>{readableAuditStatus(e)}</strong></div>
                            <div><span style={{ color: 'var(--ap-text-faint)' }}>Administrator</span><br/><strong>{e.adminEmail}</strong></div>
                            <div><span style={{ color: 'var(--ap-text-faint)' }}>Time</span><br/><strong>{new Date(e.timestamp).toLocaleString()}</strong></div>
                            <div><span style={{ color: 'var(--ap-text-faint)' }}>Request</span><br/><strong>{e.method} {e.path}</strong></div>
                            <div><span style={{ color: 'var(--ap-text-faint)' }}>IP address</span><br/><strong>{e.ip || 'Not recorded'}</strong></div>
                            <div><span style={{ color: 'var(--ap-text-faint)' }}>Technical status</span><br/><strong>{e.statusCode || '—'}</strong></div>
                          </div>
                          {e.meta && Object.keys(e.meta).length > 0 && <div style={{ marginTop: 12, fontSize: 11.5 }}><span style={{ color: 'var(--ap-text-faint)' }}>Additional details</span><pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: '5px 0 0', padding: 10, borderRadius: 8, background: 'var(--ap-surface-2)' }}>{JSON.stringify(e.meta, null, 2)}</pre></div>}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderTop: '1px solid var(--ap-border)', fontSize: 12 }}>
            <span style={{ color: 'var(--ap-text-muted)' }}>{total} total</span>
            <div className="ap-btn-row">
              <button className="ap-btn ap-btn-ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <span style={{ padding: '0 8px' }}>Page {page} of {Math.max(1, Math.ceil(total / perPage))}</span>
              <button className="ap-btn ap-btn-ghost" disabled={page >= Math.ceil(total / perPage)} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SystemHealthTab({ showAlert }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = (refresh) => {
    if (refresh) setRefreshing(true);
    return api.adminGetSystemHealth(refresh)
      .then(setHealth)
      .catch(e => showAlert(e.message, 'error'))
      .finally(() => { setLoading(false); setRefreshing(false); });
  };
  useEffect(() => { load(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="spinner">Loading system health...</div>;
  if (!health) return null;

  const pct = health.cloudinary?.credits?.percent;
  const barColor = pct >= 95 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#22c55e';

  return (
    <div className="card">
      <div className="section-header">
        <h2>♥ System Health</h2>
        <button className="ap-btn ap-btn-secondary" onClick={() => load(true)} disabled={refreshing}>
          {refreshing ? 'Refreshing…' : '↻ Refresh Cloudinary usage'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginTop: 16 }}>
        {/* Cloudinary */}
        <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: 18 }}>
          <h3 style={{ fontSize: 14, margin: '0 0 10px' }}>☁ Cloudinary</h3>
          {!health.servicesConfigured.cloudinary ? (
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Not configured.</p>
          ) : health.cloudinary.configured === false ? (
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Usage data unavailable right now.</p>
          ) : (
            <>
              <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{pct != null ? `${pct}%` : '—'}</div>
              <div style={{ height: 8, background: 'var(--border)', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
                <div style={{ height: '100%', width: `${Math.min(pct || 0, 100)}%`, background: barColor }} />
              </div>
              <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: 0 }}>
                {health.cloudinary.credits.used}/{health.cloudinary.credits.limit} credits · {health.cloudinary.plan} plan
                {health.cloudinary.stale && ' · (last known value, refresh failed)'}
              </p>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                Native business uploads only — federation partner product images never touch this account.
              </p>
            </>
          )}
        </div>

        {/* Usage this month */}
        <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: 18 }}>
          <h3 style={{ fontSize: 14, margin: '0 0 10px' }}>📊 Usage this month</h3>
          <p style={{ fontSize: 12.5, margin: '4px 0' }}>Groq AI calls: <strong>{health.usageThisMonth.aiGroqCalls}</strong></p>
          <p style={{ fontSize: 12.5, margin: '4px 0' }}>Emails drafted: <strong>{health.usageThisMonth.emailsQueued}</strong></p>
          <p style={{ fontSize: 12.5, margin: '4px 0' }}>Pending in Email Outbox: <strong>{health.outbox.pending}</strong></p>
        </div>

        {/* Server */}
        <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: 18 }}>
          <h3 style={{ fontSize: 14, margin: '0 0 10px' }}>🖥 Server</h3>
          <p style={{ fontSize: 12.5, margin: '4px 0' }}>Uptime: <strong>{Math.floor(health.server.uptimeSeconds / 3600)}h {Math.floor((health.server.uptimeSeconds % 3600) / 60)}m</strong></p>
          <p style={{ fontSize: 12.5, margin: '4px 0' }}>Memory: <strong>{health.server.memoryMb} MB</strong></p>
          <p style={{ fontSize: 12.5, margin: '4px 0' }}>Node: <strong>{health.server.nodeVersion}</strong></p>
        </div>

        {/* Service config status */}
        <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: 18 }}>
          <h3 style={{ fontSize: 14, margin: '0 0 10px' }}>🔌 Services configured</h3>
          {Object.entries(health.servicesConfigured).map(([key, configured]) => (
            <p key={key} style={{ fontSize: 12.5, margin: '4px 0', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ textTransform: 'capitalize' }}>{key}</span>
              <span style={{ color: configured ? '#22c55e' : '#9ca3af' }}>{configured ? '✓ Configured' : '— Not set'}</span>
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}
